import { DriveFolderUpload } from '@mui/icons-material';
import { Box, Typography, alpha } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { UPLOAD_DROPZONE_OPTIONS } from '~/components/UploadDialog';
import { useUploadDispatch, useUploadState } from '~/contexts/upload';

export interface ContentDropTargetProps {
  folderId: number;
}

/** Covers the screen while files from outside the browser are dragged over the window. */
export const ContentDropTarget = ({ folderId }: ContentDropTargetProps) => {
  const { t } = useTranslation();
  const { dialogOpen } = useUploadState();
  const uploadDispatch = useUploadDispatch();
  const [dragging, setDragging] = useState(false);
  // Dragging an image or link on the page also carries files; only external drags should count.
  const internalDrag = useRef(false);

  // The open upload dialog takes drops itself.
  useEffect(() => {
    if (dialogOpen) return;
    const onDragStart = () => (internalDrag.current = true);
    // A drag source removed mid-drag never sees dragend, so a drop also ends the drag.
    const onDragEnd = () => (internalDrag.current = false);
    const onDragEnter = (event: DragEvent) => {
      if (internalDrag.current || !event.dataTransfer?.types.includes('Files')) return;
      // Another open dialog owns the screen; the upload dialog shouldn't open over it.
      if (document.querySelector('[role="dialog"]')) return;
      setDragging(true);
    };
    window.addEventListener('dragstart', onDragStart);
    window.addEventListener('dragend', onDragEnd);
    window.addEventListener('drop', onDragEnd);
    window.addEventListener('dragenter', onDragEnter);
    return () => {
      window.removeEventListener('dragstart', onDragStart);
      window.removeEventListener('dragend', onDragEnd);
      window.removeEventListener('drop', onDragEnd);
      window.removeEventListener('dragenter', onDragEnter);
    };
  }, [dialogOpen]);

  const { getRootProps } = useDropzone({
    ...UPLOAD_DROPZONE_OPTIONS,
    noClick: true,
    noKeyboard: true,
    onDragLeave: () => setDragging(false),
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length)
        uploadDispatch({ type: 'setDialogOpen', dialogOpen: true, folderId, files: acceptedFiles });
    },
  });

  if (!dragging) return null;

  return (
    <Box
      // Hide on the drop itself: the dropzone reports files only once it has read a dropped folder,
      // and never if reading fails.
      {...getRootProps({ onDrop: () => setDragging(false) })}
      sx={{
        alignItems: 'center',
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
        border: '3px dashed',
        borderColor: 'primary.main',
        display: 'flex',
        flexDirection: 'column',
        inset: 0,
        justifyContent: 'center',
        position: 'fixed',
        zIndex: 'modal',
        '& > *': { pointerEvents: 'none' },
      }}
    >
      <DriveFolderUpload sx={{ fontSize: 64, color: 'primary.main', mb: 1.5 }} />
      <Typography variant="h5">{t('components.upload_dialog.messages.drop_message')}</Typography>
    </Box>
  );
};
